import React, { useEffect, useState } from 'react';
import { Sparkles, Infinity as InfinityIcon, Loader2, Zap, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * AiGenerationsPanel
 * Transparent breakdown of a tenant's AI Generations:
 *  - monthly allowance included with their plan (resets each month)
 *  - non-expiring top-up balance
 * Plus one-time top-up packs they can purchase.
 */
const AiGenerationsPanel = ({ open, onOpenChange }) => {
  const [status, setStatus] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get('/ai/credits'),
          api.get('/ai/packages'),
        ]);
        if (!cancelled) {
          setStatus(s.data);
          setPackages(p.data.packages || []);
        }
      } catch (e) {
        if (!cancelled) toast.error('Failed to load AI Generations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const buy = async (pkgId) => {
    setBuying(pkgId);
    try {
      const { data } = await api.post(`/ai/purchase-credits?package=${pkgId}`);
      window.open(data.url, '_blank');
      toast.success('Opening secure checkout…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setBuying(null);
    }
  };

  const unlimited = status?.unlimited;
  const resetDate = status?.resets_on
    ? new Date(status.resets_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  const pct = status && status.monthly_limit > 0
    ? Math.min(100, Math.round((status.monthly_used / status.monthly_limit) * 100))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ai-generations-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={18} className="text-violet-600" />
            AI Generations
          </DialogTitle>
          <DialogDescription>
            Used by the AI Writing Assistant. 1 generation = 1 prompt or refine.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin text-violet-600" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Balance summary */}
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-violet-50 to-white p-4">
              {unlimited ? (
                <div className="flex items-center gap-2 text-violet-700" data-testid="ai-gen-unlimited">
                  <InfinityIcon size={22} />
                  <span className="text-lg font-semibold">Unlimited generations</span>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-500">Available now</span>
                    <span className="text-2xl font-bold text-slate-900" data-testid="ai-gen-total">
                      {status.total_remaining}
                    </span>
                  </div>
                  {/* Monthly allowance bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Monthly plan allowance</span>
                      <span>{status.monthly_used} / {status.monthly_limit} used</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    {resetDate && (
                      <div className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-400">
                        <CalendarClock size={11} /> Resets {resetDate}
                      </div>
                    )}
                  </div>
                  {/* Top-up balance */}
                  <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
                    <span className="text-slate-500">Top-up balance (never expires)</span>
                    <span className="font-semibold text-slate-800" data-testid="ai-gen-topup">{status.topup_balance}</span>
                  </div>
                </>
              )}
            </div>

            {/* Top-up packs */}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Need more? Top-up packs
              </p>
              <div className="space-y-2">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="flex items-center justify-between rounded-lg border border-slate-200 p-3 hover:border-violet-300 transition-colors"
                    data-testid={`ai-gen-pack-${pkg.id}`}
                  >
                    <div>
                      <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                        <Zap size={14} className="text-violet-500" />
                        {pkg.credits.toLocaleString()} generations
                      </div>
                      <div className="text-xs text-slate-400">
                        ${pkg.price} · ${pkg.per_generation?.toFixed?.(3) ?? pkg.per_generation} each
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => buy(pkg.id)}
                      disabled={buying === pkg.id}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid={`ai-gen-buy-${pkg.id}`}
                    >
                      {buying === pkg.id ? <Loader2 size={14} className="animate-spin" /> : `$${pkg.price}`}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AiGenerationsPanel;
